import React, { useState, useEffect, useRef } from "react";
import "./EditingQuoteForm.css";
import Loader from "../../../Template/Loader/Loader";
import CommonMessageModal from "../../CommonMessageModal/CommonMessageModal";
import { createHourlyOrEditingQuote } from "../../../utils/APIs/bookingApis";
import { useNavigate } from "react-router-dom";
import NavigationButtons from "../../../Template/NavigationButtons/NavigationButtons";

const metroCities = [
  "Ahmedabad", "Amritsar", "Bengaluru", "Bhopal", "Bhubaneswar", "Chandigarh",
  "Chennai", "Coimbatore", "Dehradun", "Delhi", "Faridabad", "Ghaziabad",
  "Gurugram", "Guwahati", "Hyderabad", "Indore", "Jaipur", "Jalandhar",
  "Kanpur", "Kochi", "Kolkata", "Lucknow", "Ludhiana", "Madurai", "Meerut",
  "Mumbai", "Mysuru", "Nagpur", "Nashik", "New Delhi", "Noida", "Patna",
  "Pune", "Raipur", "Rajkot", "Ranchi", "Surat", "Thane", "Thiruvananthapuram",
  "Vadodara", "Varanasi", "Vijayawada", "Visakhapatnam", "Other"
];

const EditingQuoteForm = () => {
  const navigate = useNavigate();

  const editingOptions = [
    { id: "photos", label: "Photos" },
    { id: "reel", label: "Reel < 60sec" },
    { id: "video", label: "Video < 1 min" }
  ];

  const [formData, setFormData] = useState({
    clientName: "",
    editingRequired: [], // Array for checkboxes
    location: "",
    additionalDetails: "",
    phoneNumber: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState("");

  // Dropdown States
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [isOtherCity, setIsOtherCity] = useState(false);
  const cityDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCities = metroCities
    .filter((city) => city.toLowerCase().includes(citySearchTerm.toLowerCase()))
    .sort((a, b) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });

  const handleModalClose = () => {
    setShowMessage(false);
    navigate("/myProfile", {
      state: { openTab: "quotes" },
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (optionId) => {
    setFormData((prev) => {
      const current = prev.editingRequired;
      if (current.includes(optionId)) {
        return { ...prev, editingRequired: current.filter(id => id !== optionId) };
      } else {
        return { ...prev, editingRequired: [...current, optionId] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.editingRequired.length === 0) {
      alert("Please select at least one editing requirement.");
      return;
    }
    if (!formData.location) {
      alert("Please select or enter a city.");
      return;
    }

    const payload = {
      name: formData.clientName,
      city: formData.location,
      requirement: formData.editingRequired,
      additionalDetails: formData.additionalDetails,
      serviceCategory: "editing"
    };

    try {
      setLoading(true);
      const response = await createHourlyOrEditingQuote(payload);
      setMessage(response?.data?.message || "Editing quote submitted successfully");
      setShowMessage(true);
    } catch (error) {
      setMessage(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="budget-wrapper d-flex justify-content-center align-items-center" style={{ position: 'relative' }}>
        <NavigationButtons />
        <div className="container">
          <h1 className="title text-center mb-5">
            Get your Personalized Editing Quote
          </h1>

          <div className="budget-card mx-auto">
            <form onSubmit={handleSubmit}>
              
              <div className="budget-row">
                <label className="budget-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="clientName"
                  className="form-control budget-input"
                  placeholder="Enter your name"
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="budget-row">
                <label className="budget-label">
                  Editing Required <span className="text-danger">*</span>
                </label>
                <div className="budget-options">
                  {editingOptions.map((opt) => (
                    <label key={opt.id} className="budget-option-item">
                      <input
                        type="checkbox"
                        checked={formData.editingRequired.includes(opt.id)}
                        onChange={() => handleCheckboxChange(opt.id)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="budget-row">
                <label className="budget-label">
                  City <span className="text-danger">*</span>
                </label>
                <div className="custom-dropdown-wrapper" ref={cityDropdownRef} style={{ width: "100%" }}>
                  {isOtherCity ? (
                    <input
                      type="text"
                      className="form-control budget-input"
                      placeholder="Enter your city name"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      onBlur={() => { if (!formData.location) setIsOtherCity(false); }}
                    />
                  ) : (
                    <>
                      <input
                        type="text"
                        className="form-control budget-input"
                        placeholder="Search or select city"
                        value={formData.location}
                        onFocus={() => setShowCityDropdown(true)}
                        onChange={(e) => {
                          setFormData({ ...formData, location: e.target.value });
                          setCitySearchTerm(e.target.value);
                          setShowCityDropdown(true);
                        }}
                      />
                      {showCityDropdown && (
                        <div className="custom-dropdown-menu">
                          {filteredCities.map((city, index) => (
                            <div
                              key={index}
                              className="custom-dropdown-item"
                              onClick={() => {
                                if (city === "Other") {
                                  setIsOtherCity(true);
                                  setFormData({ ...formData, location: "" });
                                } else {
                                  setFormData({ ...formData, location: city });
                                }
                                setCitySearchTerm("");
                                setShowCityDropdown(false);
                              }}
                            >
                              {city}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="budget-row">
                <label className="budget-label">
                  Additional Details
                </label>
                <textarea
                  name="additionalDetails"
                  className="form-control budget-input"
                  placeholder="Special Instructions"
                  onChange={handleChange}
                  style={{ height: '100px', padding: '12px' }}
                />
              </div>

              <div className="text-center mt-5">
                <button type="submit" className="btn buttons w-50">
                  Submit Editing Quote
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>

      <CommonMessageModal
        show={showMessage}
        onClose={handleModalClose}
        message={message}
      />
    </>
  );
};

export default EditingQuoteForm;
